import { Package } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { Tag } from '@/components/ui/Tag'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type { CaseCargo } from '@/types/domain'

/** The declared load, then what the scales said about it. The declared total
 *  and the control weigh sit in the same column as the lines they contest, so
 *  a +530 kg discrepancy is read against the declaration that produced it. */
export function CargoCard({ cargo }: { cargo: CaseCargo }) {
  const { t } = useTranslation()

  /* Weight and volume as one right-hand figure, whichever of the two exist —
     a line measured only by weight must not render a dangling separator. */
  const measures = (weightKg?: string, volumeCbm?: string) =>
    [
      weightKg ? `${formatNumber(weightKg)} ${t('cases.overview.kg')}` : null,
      volumeCbm ? `${formatNumber(volumeCbm, 1)} ${t('cases.overview.cbm')}` : null,
    ]
      .filter(Boolean)
      .join(' · ')

  const measured = cargo.measured
  /* The sign decides the tone, the NUMBER is served (AD-9): the subtraction
     happened where the decimals are exact. Over the declaration is what the
     border argues about; under it is a curiosity. */
  const over = measured ? Number(measured.deltaKg) > 0 : false

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package size={15} className="text-text-muted" aria-hidden />
          {t('cases.overview.cargo')}
        </CardTitle>
      </CardHeader>
      <CardBody className="pt-2">
        <ul className="flex flex-col">
          {cargo.lines.map((line) => (
            <li
              key={line.id}
              className="border-line-hair flex flex-wrap items-center gap-x-3 gap-y-1 border-b py-2.5"
            >
              <span className="text-text-heading flex items-center gap-2 text-[13px]">
                {line.description}
                {/* Dangerous goods are flagged where the cargo is named — the
                    IMO class is why this line needs an MSDS on file and a
                    different truck at the border. */}
                {line.imoClass ? (
                  <Tag tone="attention">
                    {t('cases.overview.imo', { class: line.imoClass })}
                  </Tag>
                ) : null}
              </span>
              <span className="flex-1" />
              <span className="asa-num text-text-body text-[12.5px] whitespace-nowrap">
                {measures(line.weightKg, line.volumeCbm)}
              </span>
            </li>
          ))}

          <li className="border-line-hair flex flex-wrap items-center gap-x-3 gap-y-1 border-b py-2.5 last:border-b-0">
            <span className="text-text-body text-[13px]">
              {t('cases.overview.declaredTotal')}
            </span>
            <span className="flex-1" />
            <span className="asa-num text-text-heading text-[12.5px] font-semibold whitespace-nowrap">
              {measures(cargo.declaredWeightKg, cargo.declaredVolumeCbm)}
            </span>
          </li>

          {measured ? (
            <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5">
              <span className="text-text-body text-[13px]">
                {t('cases.overview.measuredAt', { location: measured.location })}
              </span>
              <span className="flex-1" />
              <span
                className={cn(
                  'asa-num text-[12.5px] font-semibold whitespace-nowrap',
                  over ? 'text-attention' : 'text-text-heading',
                )}
              >
                {formatNumber(measured.weightKg)} {t('cases.overview.kg')} (
                {over ? '+' : ''}
                {formatNumber(measured.deltaKg)})
              </span>
            </li>
          ) : null}
        </ul>
      </CardBody>
    </Card>
  )
}
